import { FadeUp, MaskedLine, SectionTag } from "../components/Reveal";
import { CLINIC, IMAGES, PRACTITIONER, DISCIPLINES } from "../data/clinic";
import { Phone, Check } from "lucide-react";
import drSougata from "../assets/dr-sougata.webp";

const CHAPTERS = [
    {
        num: "01",
        title: "A Skilled Medical Team",
        body: "Optiplus is led by Sougata Chatterjee, a consulting optometrist with 36+ years of experience who has practiced at Optiplus since 1990 — backed by a team of highly skilled professionals who stay current with the latest advancements, so every diagnosis is accurate and every treatment is modern.",
        testId: "about-chapter-01-expertise",
    },
    {
        num: "02",
        title: "Patient-Centered Care",
        body: "We understand that every patient is unique, with specific needs and preferences. Our professionals take the time to listen, to understand your concerns, and to build a personalised care plan that fits your life — never a one-size-fits-all prescription.",
        img: IMAGES.consult,
        testId: "about-chapter-02-patient-care",
    },
    {
        num: "03",
        title: "Rooted in the Community",
        body: "We are actively involved in Naktala and Netaji Nagar — participating in local events that promote eye health, and partnering with local organisations to provide free eye exams and eyewear to underprivileged individuals.",
        testId: "about-chapter-03-community",
    },
    {
        num: "04",
        title: "Insurance & Payment, Simplified",
        body: "We accept most major insurance plans, and our staff will help you understand your coverage. Prefer to pay directly? Choose cash, credit card or debit card — whatever suits you best.",
        testId: "about-chapter-04-insurance",
    },
    {
        num: "05",
        title: "State-of-the-Art Technology",
        body: "From computerised refraction to high-definition slit-lamp microscopy, our clinic is equipped to see what the naked eye cannot. Precise instruments mean precise prescriptions — and earlier detection of silent conditions like glaucoma.",
        img: IMAGES.frames,
        testId: "about-chapter-05-technology",
    },
];

const About = () => (
    <main data-testid="about-page" className="pt-32">
        <section className="relative mx-auto max-w-7xl overflow-hidden px-4 pb-20 sm:px-8 lg:px-16 lg:pb-28">
            <div className="noise-overlay pointer-events-none absolute inset-0 opacity-20" />
            <SectionTag className="relative mb-8">About Optiplus</SectionTag>
            <h1
                data-testid="about-hero-title"
                className="font-display relative max-w-4xl text-4xl font-light leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl"
            >
                <MaskedLine>Pioneering ophthalmic</MaskedLine>
                <MaskedLine delay={0.12}>
                    precision in <em className="text-emerald-400">South Kolkata</em>
                </MaskedLine>
            </h1>
            <FadeUp delay={0.3} className="relative mt-8 max-w-2xl">
                <p
                    data-testid="about-hero-description"
                    className="text-base font-light leading-relaxed text-[#a3b8a8] sm:text-lg"
                >
                    Located beside Shakti Sangha Club in Naktala, Optiplus is a dedicated eye-care
                    clinic providing high-quality services to patients of all ages — from
                    comprehensive eye exams and contact lens fittings to surgical procedures that
                    support long-term health and well-being.
                </p>
            </FadeUp>

            <FadeUp delay={0.4} className="relative mt-16">
                <div
                    data-testid="about-disciplines-ancillaries"
                    className="rounded-3xl border border-emerald-500/20 bg-[#101f15]/70 p-8 lg:p-12"
                >
                    <p className="text-xs font-mono tracking-[0.3em] uppercase text-emerald-400">
                        Discipline &amp; Ancillaries
                    </p>
                    <ul className="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                        {DISCIPLINES.map((d) => (
                            <li key={d} className="flex items-start gap-3 text-sm font-light leading-relaxed text-[#a3b8a8]">
                                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                                {d}
                            </li>
                        ))}
                    </ul>
                </div>
            </FadeUp>
        </section>

        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16 lg:pb-32">
            <div className="space-y-24 lg:space-y-36">
                {CHAPTERS.map((c, i) => (
                    <div
                        key={c.testId}
                        data-testid={c.testId}
                        className="grid items-start gap-10 lg:grid-cols-12 lg:gap-16"
                    >
                        <div className="lg:col-span-3">
                            <div className="lg:sticky lg:top-32">
                                <FadeUp>
                                    <span className="font-display block text-7xl font-light text-emerald-500/25 lg:text-8xl">
                                        {c.num}
                                    </span>
                                    <span className="mt-2 block h-px w-16 bg-emerald-500/50" />
                                </FadeUp>
                            </div>
                        </div>
                        <div className="lg:col-span-9">
                            <FadeUp delay={0.05}>
                                <h2 className="font-display text-3xl font-light tracking-tight sm:text-4xl">
                                    {c.title}
                                </h2>
                                <p className="mt-5 max-w-2xl text-base font-light leading-relaxed text-[#a3b8a8]">
                                    {c.body}
                                </p>
                                
                            </FadeUp>
                            {c.img && (
                                <FadeUp delay={0.15} className="mt-10">
                                    <div className="group relative overflow-hidden rounded-3xl border border-emerald-500/15">
                                        <img
                                            src={c.img}
                                            alt={c.title}
                                            className="emerald-duotone aspect-[21/9] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-emerald-950/30 mix-blend-multiply" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#060a07]/70 to-transparent" />
                                    </div>
                                </FadeUp>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <FadeUp className="mt-28 rounded-3xl border border-emerald-500/20 bg-[#101f15]/70 p-10 text-center lg:p-16">
                <p className="text-xs font-mono tracking-[0.3em] uppercase text-emerald-400">
                    Come see the difference
                </p>
                <h2 className="font-display mx-auto mt-4 max-w-2xl text-3xl font-light leading-tight tracking-tight sm:text-4xl">
                    Your eyes deserve a clinic that treats you like a person, not a number.
                </h2>
                <a
                    href={CLINIC.phoneHref}
                    data-testid="about-cta-call"
                    className="mt-8 inline-flex items-center gap-3 rounded-full bg-emerald-500 px-8 py-4 text-sm font-semibold text-[#04120a] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(34,197,94,0.45)]"
                >
                    <Phone className="h-4 w-4" /> Call to Book · {CLINIC.phoneDisplay}
                </a>
            </FadeUp>
        </section>
    </main>
);

export default About;
